import { api } from '../../../lib/api';
import type { FacetValue, ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CreateStockBatchRequest,
  CreateStockUnitRequest,
  DisposeStockRequest,
  RegisterStockUnitsRequest,
  StockAdjustmentRequest,
  ReturnStockUnitRequest,
  StockBatchDetail,
  StockBatchListItem,
  StockBatchListQuery,
  StockLevelAging,
  StockLevelGroupFigures,
  StockLevelGroupRow,
  StockLevelListItem,
  StockLevelListQuery,
  StockMovementListItem,
  StockMovementListQuery,
  StockUnitDetail,
  StockUnitListItem,
  StockUnitListQuery,
  UpdateStockBatchRequest,
  UpdateStockUnitRequest,
  WriteOffStockUnitRequest,
} from './stockTypes';

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

/** Both stock lists carry the same three axes, so they serialize through one function. */
function stockQuery(query: StockBatchListQuery | StockUnitListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.item !== undefined) params.set('item', query.item);
  if (query.facility !== undefined) params.set('facility', query.facility);
  if (query.condition !== undefined) params.set('condition', query.condition);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Lots of quantity-tracked stock.
 *
 * <b>There is no endpoint here that writes a ledger line.</b> Every movement is a consequence
 * of one of these calls, which is what makes `stock-movements` a read-only module rather than
 * one whose create key nobody happened to use.
 */
export const stockBatchesApi = {
  list: (query: StockBatchListQuery, signal?: AbortSignal) =>
    api<PagedResult<StockBatchListItem>>(`/api/stock-batches${stockQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<StockBatchDetail>(`/api/stock-batches/${id}`, { signal }),

  /** Receives a lot — the row and its opening receipt, in one transaction. */
  create: (request: CreateStockBatchRequest) =>
    api<StockBatchDetail>('/api/stock-batches', { method: 'POST', body: request }),

  /**
   * A stock ADJUSTMENT: corrects the lot carrying that batch number against a physical count,
   * or creates one where the record has none.
   *
   * <b>The same key as `create`</b>, and it is the key the restock this replaced carried: the
   * operation can bring a lot into existence, so the wider of the two privileges is the honest
   * one to require. What changed is the CLAIM — a signed `Adjustment` rather than a `Receipt`.
   * It returns the lot it landed on either way.
   */
  adjust: (request: StockAdjustmentRequest) =>
    api<StockBatchDetail>('/api/stock-batches/adjust', { method: 'POST', body: request }),

  /** A changed quantity appends a ledger adjustment; an unchanged one writes no line. */
  update: (id: string, request: UpdateStockBatchRequest) =>
    api<StockBatchDetail>(`/api/stock-batches/${id}`, { method: 'PUT', body: request }),

  /** Its own key — destroying stock and correcting a note are not the same privilege. */
  dispose: (id: string, request: DisposeStockRequest) =>
    api<StockBatchDetail>(`/api/stock-batches/${id}/dispose`, { method: 'POST', body: request }),

  /** Refused while the lot still holds stock: a delete must not hide stock from every total. */
  deactivate: (id: string) => api<void>(`/api/stock-batches/${id}`, { method: 'DELETE' }),

  /** Owes no clash check — a batch number carries no unique index. */
  activate: (id: string) => api<void>(`/api/stock-batches/${id}/restore`, { method: 'POST' }),

  /** Refused outright while any live lot still holds stock. */
  clear: () => api<number>('/api/stock-batches/clear', { method: 'POST' }),
};

/** Individually serial-numbered things, tracked one at a time. */
export const stockUnitsApi = {
  list: (query: StockUnitListQuery, signal?: AbortSignal) =>
    api<PagedResult<StockUnitListItem>>(`/api/stock-units${stockQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<StockUnitDetail>(`/api/stock-units/${id}`, { signal }),

  create: (request: CreateStockUnitRequest) =>
    api<StockUnitDetail>('/api/stock-units', { method: 'POST', body: request }),

  /**
   * Receives several serialized things of one item at once, and answers with how many.
   *
   * The SAME key as `create` — it is that privilege performed once or twelve times. What it
   * adds is atomicity, which is the whole reason the form posts here rather than looping the
   * single create: a refused row must not leave the rows before it recorded.
   */
  register: (request: RegisterStockUnitsRequest) =>
    api<number>('/api/stock-units/register', { method: 'POST', body: request }),

  /** Moves the status only between states that still HOLD the unit. */
  update: (id: string, request: UpdateStockUnitRequest) =>
    api<StockUnitDetail>(`/api/stock-units/${id}`, { method: 'PUT', body: request }),

  /** Off the owned tally. A condemnation is a disposal; a theft is an adjustment. */
  writeOff: (id: string, request: WriteOffStockUnitRequest) =>
    api<StockUnitDetail>(`/api/stock-units/${id}/write-off`, { method: 'POST', body: request }),

  /**
   * Back on the tally, as an adjustment.
   *
   * The SAME key as the write-off, not the lighter restore one: this moves stock, and deciding
   * what is on the owned tally is one privilege in both directions.
   */
  returnToStock: (id: string, request: ReturnStockUnitRequest) =>
    api<StockUnitDetail>(`/api/stock-units/${id}/return-to-stock`, { method: 'POST', body: request }),

  /** Refused while the unit is still on the tally — write it off first. */
  deactivate: (id: string) => api<void>(`/api/stock-units/${id}`, { method: 'DELETE' }),

  /** Owes a serial clash check: the unique index is filtered, so a delete frees the serial. */
  activate: (id: string) => api<void>(`/api/stock-units/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/stock-units/clear', { method: 'POST' }),
};

/**
 * What is on hand, available, low and near expiry — derived, per item.
 *
 * **ONE call, and the absences are the design.** There is no create, update or delete here
 * because there is no row: every figure is computed at read time, so the module declares only
 * `read` and this client has only a list.
 */
/**
 * The query string all four stock-level calls share.
 *
 * ONE serialiser, because the four answer different halves of ONE question: a summary
 * counted over a different filter from the groups beneath it would put a badge over a tab
 * whose own rows disagree with it.
 *
 * Present-but-empty is "none of these" and matches no row, so each axis is written on the KEY
 * being present rather than on the value being truthy — the three-state facet contract.
 */
function levelParams(query: StockLevelListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.kind !== undefined) params.set('kind', query.kind);
  if (query.category !== undefined) params.set('category', query.category);
  if (query.stock !== undefined) params.set('stock', query.stock);
  if (query.tenure !== undefined) params.set('tenure', query.tenure);
  if (query.alert !== undefined) params.set('alert', query.alert);

  /*
    ONE group being expanded, and a plain truthy test is right here where it would be wrong
    one line above: this is not a facet, so it has no ticked-to-nothing state to express.
  */
  if (query.group) params.set('group', query.group);

  /*
    The AXIS the group id is read against. Written only when it departs from the default, so
    the four calls on an ordinary tab send the query string they always did.
  */
  if (query.groupBy) params.set('groupBy', query.groupBy);

  const serialized = params.toString();

  return serialized ? `?${serialized}` : '';
}

/**
 * FOUR CALLS, in the order a reader can use an answer — product call, 28 Sep 2026.
 *
 * The page used to ask one question: one row per item, every figure a correlated subquery,
 * every group summed client-side. Measured against SQL Server that was 15 KB of SQL and 63
 * correlated SELECTs, about 2.7 s before anything appeared — on a table that opens COLLAPSED,
 * so the figures under every group nobody expanded were computed and thrown away.
 *
 * Now: the badges are asked for first and awaited by nothing, the GROUPS draw the table in
 * under 100 ms, each group fetches its OWN figures, and a group expanded fetches its children.
 */
export const stockLevelsApi = {
  /** Step 3 — one group’s CHILDREN, on expand. The only call that runs the whole projection. */
  list: (query: StockLevelListQuery, signal?: AbortSignal) =>
    api<PagedResult<StockLevelListItem>>(`/api/stock-levels${levelParams(query)}`, { signal }),

  /**
   * Step 0 — ONE TAB’S BADGE, and the page calls it once per tab.
   *
   * <b>Issued before anything else on the screen.</b> A badge answers whether there is
   * anything waiting on a tab the reader is NOT standing on, which is the question they ask
   * before they ask what is on the one they are — so nothing goes out in front of it.
   *
   * <b>One call per tab rather than one for all five.</b> They were a loop inside the
   * summary, so no badge could be drawn until every bucket was counted; split, each appears
   * as its own count lands. That is the reasoning that already gave each GROUP its own
   * figures call, applied one level up.
   *
   * The query names the TAB — its kind and its tenure. `stock` and `alert` ride along and
   * are ignored by the server, deliberately: those two axes are the attention question this
   * badge answers, and the server is where that rule lives rather than in every caller.
   */
  attention: (query: StockLevelListQuery, signal?: AbortSignal) =>
    api<number>(`/api/stock-levels/attention${levelParams(query)}`, { signal }),

  /**
   * Step 0b — the ALERT TILES and the filter rail. It carries no badge axis any more.
   *
   * Fired FIRST and awaited by nothing. It is the most expensive of the four — every axis
   * counted against every other one, two of them derived — so blocking the table on it would
   * give back everything the split buys.
   */
  summary: (query: StockLevelListQuery, signal?: AbortSignal) =>
    api<Record<string, FacetValue[]>>(`/api/stock-levels/summary${levelParams(query)}`, { signal }),

  /** Step 1 — the item TYPES, with their counts and no figures. This is what draws the table. */
  groups: (query: StockLevelListQuery, signal?: AbortSignal) =>
    api<PagedResult<StockLevelGroupRow>>(`/api/stock-levels/groups${levelParams(query)}`, { signal }),

  /**
   * Step 2 — ONE group’s summed figures, called per row so a slow one waits alone.
   *
   * A group the filters emptied answers 404, and the caller reads that as "no figures" rather
   * than as a row of zeros: zero would claim the type holds nothing, where the honest answer
   * is that nothing here matches.
   */
  groupFigures: (typeId: string, query: StockLevelListQuery, signal?: AbortSignal) =>
    api<StockLevelGroupFigures>(
      `/api/stock-levels/groups/${typeId}/figures${levelParams(query)}`,
      { signal },
    ),

  /**
   * Recomputes every item's stored figures from the lots, units, issuances and ledger, and
   * answers how many rows were written.
   *
   * **The repair behind the cache.** Eleven of the figures on this register are stored columns a
   * write maintains rather than subqueries run on every read. Nothing increments them — every
   * write recomputes the whole row from the base tables — so a maintained row can only ever be
   * STALE, and this is what puts it back in step.
   *
   * **`stock-levels.rebuild`, not `read`.** It touches every item in the system in one
   * statement, where every other write in the suite moves one lot or one unit.
   */
  /**
   * The figures that AGE, for the items this query selects.
   *
   * **Its own call, so the table draws without waiting for it.** The register's own read is one
   * join over the stored figures and lands immediately; expired, near expiry, delayed return,
   * the two lease counts and the stock status each cost a correlated subquery per row, so they
   * arrive after and fill in.
   *
   * It takes the SAME query the list was given — kind, tenure, category, search and whichever
   * group is expanded — and the server drops the stock and alert axes, which filter ON these
   * values and would otherwise answer only for the rows already chosen by them.
   */
  aging: (query: StockLevelListQuery, signal?: AbortSignal) =>
    api<StockLevelAging[]>(`/api/stock-levels/aging${levelParams(query)}`, { signal }),

  rebuild: (signal?: AbortSignal) =>
    api<number>('/api/stock-levels/rebuild', { method: 'POST', signal }),
};

/**
 * The stock ledger — every change to stock, one line each.
 *
 * **ONE call, and the absences are the design.** A movement is written by the batch or unit
 * operation that moved the stock, never on its own, and the table is append-only — so there is
 * no create, no update, no delete and no clear here, and the module declares only `read`.
 */
export const stockMovementsApi = {
  list: (query: StockMovementListQuery, signal?: AbortSignal) => {
    const params = base(query, new URLSearchParams());

    // Present-but-empty is "none of these" and matches no row, so each axis is written on the
    // KEY being present rather than on the value being truthy — the three-state facet contract.
    if (query.item !== undefined) params.set('item', query.item);
    if (query.facility !== undefined) params.set('facility', query.facility);
    if (query.movementType !== undefined) params.set('movementType', query.movementType);
    if (query.direction !== undefined) params.set('direction', query.direction);

    const serialized = params.toString();
    return api<PagedResult<StockMovementListItem>>(
      `/api/stock-movements${serialized ? `?${serialized}` : ''}`,
      { signal },
    );
  },
};
